import type { DailyRiskMetricRow, RiskThreshold } from '../api/client'

// Trade-cycle win rate display for the Risk tab.
// Returns { rate, count } per stream:
//   - bga, jh, gold, gge: the fund's own FIFO cycle rate + count. The API
//               pairs cycles PER FUND since 2026-09-09 (the bgaCycle*
//               columns are the generic per-fund cycle slots); a row whose
//               cycle fields are still null renders '—'.
//   - combined: cycle-count-weighted average of all-fund + OTP cycles.
//               (bgaWr × bgaCount + otpWr × otpCount) / (bgaCount + otpCount).
//               Returns null when both cycle counts are zero.
//
// History: the combined branch was aggregating BGA + OTP per 2026-05-28,
// then was narrowed to BGA-only on 2026-08-06 ("REMOVE OTP FROM THE
// REPORT"). Restored on 2026-08-23 as part of the risk-tab baseline
// alignment (plans/playful-tickling-bunny.md): the baseline "expected"
// number is now derived from a cycle-count-weighted aggregate across every
// fund's currently-active promoted preset, so the achieved "combined"
// side must aggregate cycles the same way — otherwise the hero card
// compares apples (aggregate baseline) to oranges (BGA-only achieved).
// The per-fund `bga` row keeps its BGA-only number.
//
// Lives in a separate module (not the .vue file) because <script setup>
// cannot contain ES module exports, so unit tests need an importable
// surface.
/** Trailing windows the trade-cycle win rate is reported on — the same tiers as Sharpe (2026-10-27). */
export type CycleWindow = 20 | 60 | 120

function cycleFields(row: DailyRiskMetricRow, window: CycleWindow) {
  switch (window) {
    case 20: return { bgaWr: row.bgaCycleWinRate20d ?? null, bgaN: row.bgaCycleCount20d ?? null,
                      otpWr: row.otpCycleWinRate20d ?? null, otpN: row.otpCycleCount20d ?? null }
    case 60: return { bgaWr: row.bgaCycleWinRate60d, bgaN: row.bgaCycleCount60d,
                      otpWr: row.otpCycleWinRate60d, otpN: row.otpCycleCount60d }
    default: return { bgaWr: row.bgaCycleWinRate120d, bgaN: row.bgaCycleCount120d,
                      otpWr: row.otpCycleWinRate120d, otpN: row.otpCycleCount120d }
  }
}

export function streamCycleWinRate(
  row: DailyRiskMetricRow | null,
  window: CycleWindow = 120,
): { rate: number | null; count: number } {
  if (!row) return { rate: null, count: 0 }
  const f = cycleFields(row, window)
  if (row.stream === 'bga' || row.stream === 'jh' || row.stream === 'gold' || row.stream === 'gge' || row.stream === 'japan'
      || row.stream === 'charteris' || row.stream === 'amati' || row.stream === 'miners' || row.stream === 'japanesg' || row.stream === 'tech' || row.stream === 'electrum'
      || row.stream === 'jhtech' || row.stream === 'asiatech' || row.stream === 'baringsoil' || row.stream === 'spworld' || row.stream === 'smallcap') {
    return { rate: f.bgaWr, count: f.bgaN ?? 0 }
  }
  if (row.stream === 'combined') {
    const bgaCount = f.bgaN ?? 0
    const otpCount = f.otpN ?? 0
    const totalCount = bgaCount + otpCount
    if (totalCount === 0) return { rate: null, count: 0 }
    // A side with cycles but a null rate (the server needs >= 2 cycles to rate a window)
    // can't be weighted honestly — fall back to the other side alone, or null.
    if (bgaCount > 0 && f.bgaWr === null) return otpCount > 0 && f.otpWr !== null ? { rate: f.otpWr, count: otpCount } : { rate: null, count: totalCount }
    if (otpCount > 0 && f.otpWr === null) return { rate: f.bgaWr, count: bgaCount }
    const bgaWins = (f.bgaWr ?? 0) * bgaCount
    const otpWins = (f.otpWr ?? 0) * otpCount
    return { rate: (bgaWins + otpWins) / totalCount, count: totalCount }
  }
  // (legacy) otp stream: no per-stream trade-cycle metric exposed here.
  return { rate: null, count: 0 }
}

// ── V100 per-fund threshold scoping (2026-09-05) ──
// The Threshold Config editor shows one tab per scope. Rows without a fund
// (a pre-V100 API during rollout) belong to BGA — the scope all rows
// carried before the migration.

// JAPAN/CHARTERIS/AMATI/MINERS/JAPANESG/TECH/ELECTRUM/JHTECH/ASIATECH/BARINGSOIL/SPWORLD/SMALLCAP scopes (2026-10-11; AMATI 2026-10-13; MINERS 2026-10-25): the API returns them only to a caller who can
// view the fund — derived from the user's BGA rows × the fund's vol-calibrated scale
// (RiskThreshold.DeriveGatedScope) until saved. The panel gates their tabs on canView again.
const SCOPE_ORDER = ['BGA', 'JH', 'GOLD', 'GGE', 'JAPAN', 'CHARTERIS', 'AMATI', 'MINERS', 'JAPANESG', 'TECH', 'ELECTRUM', 'JHTECH', 'ASIATECH', 'BARINGSOIL', 'SPWORLD', 'SMALLCAP', 'COMBINED']

/** Fund-level NAV risk (buy & hold official NAV) for a gated fund's "no position yet" tile. */
export interface NavRisk {
  /** Annualised stdev of daily log returns over the window. */
  vol: number
  /** Worst peak-to-trough fall inside the window (fraction). */
  maxDd: number
  /** Fall from the window's peak to the last NAV (fraction). */
  currentDd: number
  /** Number of daily returns used. */
  days: number
  asOf: string
}

/**
 * NAV risk from a /api/nav-history/{fund} series (pct = percent change from the anchor, which is the
 * first point at 0). Null when fewer than 20 returns are available — too short to mean anything.
 */
export function navRiskFromPoints(points: ReadonlyArray<{ date: string; pct: number }>): NavRisk | null {
  const levels = points.map(p => 1 + p.pct / 100).filter(v => Number.isFinite(v) && v > 0)
  if (levels.length < 21) return null
  const rets: number[] = []
  for (let i = 1; i < levels.length; i++) rets.push(Math.log(levels[i] / levels[i - 1]))
  const mean = rets.reduce((a, b) => a + b, 0) / rets.length
  const variance = rets.reduce((a, r) => a + (r - mean) ** 2, 0) / (rets.length - 1)
  let peak = levels[0]
  let maxDd = 0
  for (const v of levels) {
    peak = Math.max(peak, v)
    maxDd = Math.max(maxDd, 1 - v / peak)
  }
  return {
    vol: Math.sqrt(variance) * Math.sqrt(252),
    maxDd,
    currentDd: 1 - levels[levels.length - 1] / peak,
    days: rets.length,
    asOf: points[points.length - 1].date,
  }
}
const METRIC_ORDER = ['sharpe', 'sortino', 'max_dd', 'win_rate', 'profit_factor', 'cagr']

function scopeOf(t: RiskThreshold): string {
  return t.fund || 'BGA'
}

export function thresholdScopes(rows: RiskThreshold[]): string[] {
  const present = new Set(rows.map(scopeOf))
  return SCOPE_ORDER.filter(s => present.has(s))
}

export function thresholdsForScope(rows: RiskThreshold[], scope: string): RiskThreshold[] {
  const rank = (m: string) => {
    const i = METRIC_ORDER.indexOf(m)
    return i === -1 ? METRIC_ORDER.length : i
  }
  return rows
    .filter(t => scopeOf(t) === scope)
    .sort((a, b) => rank(a.metric) - rank(b.metric))
}

/** 2026-10-28 (Paolo, option 2): a pot user's combined Sharpe baseline comes from their fund-of-funds backtest but only
 *  grades the colour once the live 120-day window is clear of the pot inception. Its source carries
 *  `classify_from=YYYY-MM-DD`; returns that date while it is still ahead of `now`, else null (legacy baselines: null). */
export function sharpeGradedFrom(source: string | null | undefined, now: Date = new Date()): string | null {
  const m = source ? /classify_from=(\d{4}-\d{2}-\d{2})/.exec(source) : null
  if (!m) return null
  return now.toISOString().slice(0, 10) < m[1] ? m[1] : null
}
